import { useState, useEffect, useRef } from "react";
import { Link } from "react-router-dom";
import { Loader2 } from "lucide-react";
import { useAdminPaymentIndexQuery, useReverifyPaymentMutation, useResendPaymentReceiptMutation, useGetPaymentEditRequestsQuery } from "../api/billingApi";
import { BASE_URL } from "../../../features/api/apiSlice";
import { formatCurrency } from "../../../utils/formatters";
import DataTable from "../components/DataTable";
import TableSkeleton from "../components/TableSkeleton";
import useDebounce from "../components/useDebounce";
import RequestPaymentEditModal from "../components/RequestPaymentEditModal";
import RequestPaymentDeleteModal from "../components/RequestPaymentDeleteModal";
import PaymentAuditTrailModal from "../components/PaymentAuditTrailModal";
import AccessGate from "../../../components/AccessGate";
import { PERMS } from "../../../utils/perms";

const getCategoryStyle = (categoryName: string) => {
  const name = categoryName?.toLowerCase() || "";
  if (name.includes("probationer"))
    return "bg-blue-50 text-blue-700 border-blue-100";
  if (name.includes("fellow"))
    return "bg-purple-50 text-purple-700 border-purple-100";
  if (name.includes("technician"))
    return "bg-amber-50 text-amber-700 border-amber-100";
  if (name.includes("student"))
    return "bg-slate-100 text-slate-600 border-slate-200";
  if (name.includes("past president"))
    return "bg-rose-100 text-rose-700 border-rose-200";
  if (name.includes("corporate"))
    return "bg-rose-50 text-rose-700 border-rose-100";
  return "bg-emerald-50 text-emerald-700 border-emerald-100";
};

/**
 * Who paid.
 *
 * Not every payment belongs to an account. An event fee taken from a
 * non-member at the desk is billed to nobody — `user` is null and the name
 * lives on the payment itself — so reading `user.name` alone printed a blank
 * row for real money.
 */
const payerName = (r: any) =>
  r.user?.name || r.payer_name || "—";

/** The line under the name: membership number, or what kind of payer this is. */
const payerTag = (r: any) => {
  if (r.user?.member?.membership_no) return r.user.member.membership_no;
  if (!r.user_id) return "NON-MEMBER";
  return "PROVISIONAL";
};

export default function FinanceManagement() {
  const [page, setPage] = useState(1);
  const [perPage, setPerPage] = useState(25);
  const [search, setSearch] = useState("");
  const [sort, setSort] = useState("payments.created_at");
  const [dir, setDir] = useState("desc");

  // Filter States
  const [status, setStatus] = useState("");
  const [adminId, setAdminId] = useState("");
  const [stateId, setStateId] = useState("");
  const [categoryId, setCategoryId] = useState("");
  const [chapterId, setChapterId] = useState("");
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");

  // The filter panel is always open from `lg` up; on a phone it folds away
  // behind a button so the ledger is the first thing on the screen.
  const [showFilters, setShowFilters] = useState(false);
  const activeFilterCount = [status, adminId, stateId, categoryId, chapterId, startDate, endDate]
    .filter(Boolean).length;

  const debouncedSearch = useDebounce(search);

  // Date inputs were the one filter left undebounced. A native date field fires
  // a change per keystroke while the year is being typed, so "2026" arrived as
  // 0002 → 0020 → 0202 → 2026 — four round trips, each running the paginated
  // query plus four aggregates, and the intermediate years flashed an empty
  // ledger. Debouncing settles on the date the admin actually meant.
  const debouncedStartDate = useDebounce(startDate);
  const debouncedEndDate = useDebounce(endDate);

  // Reset to page 1 whenever any filter changes to avoid "empty page" bugs
  useEffect(() => {
    setPage(1);
  }, [
    debouncedSearch,
    status,
    adminId,
    stateId,
    categoryId,
    chapterId,
    debouncedStartDate,
    debouncedEndDate,
  ]);

  const currentFilters = {
    search: debouncedSearch,
    sort_by: sort,
    sort_dir: dir,
    status,
    admin_id: adminId,
    state_id: stateId,
    user_category_id: categoryId,
    chapter_id: chapterId,
    start_date: debouncedStartDate,
    end_date: debouncedEndDate,
  };

  const { data, isLoading, refetch } = useAdminPaymentIndexQuery({
    page,
    per_page: perPage,
    ...currentFilters,
  });

  // Edit / Delete request modals
  const [editTarget, setEditTarget] = useState<any>(null);
  const [deleteTarget, setDeleteTarget] = useState<any>(null);
  const [trailTarget, setTrailTarget] = useState<any>(null);
  const [actionToast, setActionToast] = useState<{ text: string; tone: "success" | "error" } | null>(null);
  const showActionToast = (msg: string, tone: "success" | "error" = "success") => {
    setActionToast({ text: msg, tone });
    setTimeout(() => setActionToast(null), 3000);
  };

  const { data: editRequestsData } = useGetPaymentEditRequestsQuery({ status: "pending", per_page: 1 });
  const pendingRequestCount: number = editRequestsData?.requests?.total ?? editRequestsData?.total ?? 0;

  const [isExporting, setIsExporting] = useState(false);
  const [reverifyPayment] = useReverifyPaymentMutation();
  const [resendReceipt] = useResendPaymentReceiptMutation();
  const [resendingId, setResendingId] = useState<string | null>(null);

  const handleResendReceipt = async (paymentId: string) => {
    setResendingId(paymentId);
    try {
      const res: any = await resendReceipt(paymentId).unwrap();
      showActionToast(res?.message ?? "Receipt queued.");
    } catch (err: any) {
      alert(err?.data?.message || "Could not send the receipt. Please try again.");
    } finally {
      setResendingId(null);
    }
  };
  const [reverifyingId, setReverifyingId] = useState<string | null>(null);
  const [verifyingIds, setVerifyingIds] = useState<Set<string>>(new Set());
  const pollingIntervals = useRef<Record<string, ReturnType<typeof setInterval>>>({});

  const stopPolling = (paymentId: string) => {
    clearInterval(pollingIntervals.current[paymentId]);
    delete pollingIntervals.current[paymentId];
    setVerifyingIds((prev) => {
      const next = new Set(prev);
      next.delete(paymentId);
      return next;
    });
  };

  // `fromStatus` is the row's status when reverification started. Stopping on
  // "not pending" would end the poll on its first tick for a failed or
  // cancelled attempt — which are now reverifiable — so watch for the status
  // actually CHANGING instead.
  const startPolling = (paymentId: string, fromStatus: string) => {
    setVerifyingIds((prev) => new Set(prev).add(paymentId));
    let attempts = 0;
    const maxAttempts = 20; // 20 × 4s = 80s, covers worst-case 60s cron delay

    pollingIntervals.current[paymentId] = setInterval(async () => {
      attempts++;
      try {
        const result = await refetch();
        const updated = result.data?.payments?.data?.find(
          (p: any) => p.id === paymentId
        );
        if (!updated || updated.status !== fromStatus) {
          stopPolling(paymentId);
          return;
        }
      } catch {
        stopPolling(paymentId);
        return;
      }
      if (attempts >= maxAttempts) stopPolling(paymentId);
    }, 4000);
  };

  // Clean up all intervals on unmount
  useEffect(() => {
    return () => {
      Object.values(pollingIntervals.current).forEach(clearInterval);
    };
  }, []);

  const handleReverify = async (paymentId: string, fromStatus: string) => {
    setReverifyingId(paymentId);
    try {
      const res: any = await reverifyPayment(paymentId).unwrap();
      // Verification runs synchronously on the server, so this message is the
      // real outcome — say it rather than leaving the admin to read the table.
      if (res?.message) showActionToast(res.message);
      startPolling(paymentId, fromStatus);
    } catch (err: any) {
      // The API sends the underlying failure in `error`; showing only `message`
      // reduced every failure to "hit an error, check the logs" and threw away
      // the one line that says what actually went wrong.
      const base = err?.data?.message || "Reverification failed. Please try again.";
      const detail = [err?.data?.error, err?.data?.where && `(${err.data.where})`]
        .filter(Boolean)
        .join(" ");
      alert(detail ? `${base}\n\n${detail}` : base);
    } finally {
      setReverifyingId(null);
    }
  };

  const paymentList = data?.payments?.data || [];
  //console.log(paymentList, "this is**************************************");
  const pagination = data?.payments || {};
  const stats = data?.stats || {
    total_sum: 0,
    total_count: 0,
    by_status: [],
    by_category: [],
  };
  const lookups = data?.lookups || {
    admins: [],
    states: [],
    categories: [],
    chapters: [],
  };

  // The per-row buttons, shared by the desktop table's Action column and the
  // mobile card below it — the phone gets the same capabilities as the desk,
  // not a read-only view of them.
  const rowActions = (r: any) => (
    <div className="flex items-center gap-1.5 flex-wrap">
      <Link
        to={`/receipt/${r.receipt_number || r.reference}`}
        className="px-3 py-1.5 bg-slate-900 text-white text-[10px] font-black rounded-lg hover:bg-orange-600 transition-all uppercase tracking-widest"
      >
        Receipt
      </Link>
      {/* Settlement emails this automatically; the button is for the copies
          lost while the queue worker was down, which nothing retries. */}
      {r.status === "paid" && (
        <button
          onClick={() => handleResendReceipt(r.id)}
          disabled={resendingId === r.id}
          title="Email this receipt to the member again"
          className="px-2.5 py-1.5 bg-emerald-50 text-emerald-700 border border-emerald-100 text-[10px] font-black rounded-lg hover:bg-emerald-100 transition-all uppercase tracking-widest disabled:opacity-50"
        >
          {resendingId === r.id ? "..." : "Email"}
        </button>
      )}
      {/* Anything unsettled can be re-asked about, not just pending.
          OnePay declines at the counter and confirms the same transaction
          minutes later (leaving us 'failed'), and a member's retry cancels
          the previous attempt — which is often the one the money actually
          went through on. */}
      {r.status !== "paid" && !verifyingIds.has(r.id) && (
        <button
          onClick={() => handleReverify(r.id, r.status)}
          disabled={reverifyingId === r.id}
          className="px-3 py-1.5 bg-amber-500 text-white text-[10px] font-black rounded-lg hover:bg-amber-600 transition-all uppercase tracking-widest disabled:opacity-50 inline-flex items-center gap-1.5"
        >
          {reverifyingId === r.id ? (
            <>
              <Loader2 className="w-3 h-3 animate-spin" /> Verifying
            </>
          ) : (
            "Reverify"
          )}
        </button>
      )}
      {/* Only admin-recorded payments can be edited/deleted, and only by an
          admin holding the amendment-request permission. */}
      {r.recorded_by && (
        <AccessGate perm={PERMS.billing.amendmentRequest}>
          <button
            onClick={() => setEditTarget(r)}
            title="Request edit"
            className="px-2.5 py-1.5 bg-orange-50 text-orange-700 border border-orange-100 text-[10px] font-black rounded-lg hover:bg-orange-100 transition-all uppercase tracking-widest"
          >
            Edit Req
          </button>
          <button
            onClick={() => setDeleteTarget(r)}
            title="Request deletion"
            className="px-2.5 py-1.5 bg-rose-50 text-rose-700 border border-rose-100 text-[10px] font-black rounded-lg hover:bg-rose-100 transition-all uppercase tracking-widest"
          >
            Del Req
          </button>
        </AccessGate>
      )}
      {/* The trail exposes why settled records were changed — same read
          permission as the amendment queue itself. */}
      <AccessGate perm={PERMS.billing.amendmentAccess}>
        <button
          onClick={() => setTrailTarget(r)}
          title="View audit trail"
          className="px-2.5 py-1.5 bg-slate-100 text-slate-600 text-[10px] font-black rounded-lg hover:bg-slate-200 transition-all uppercase tracking-widest"
        >
          Trail
        </button>
      </AccessGate>
    </div>
  );

  const columns = [
    {
      key: "receipt_number",
      header: "Receipt #",
      accessor: (r: any) => (
        <span className="font-mono font-bold text-slate-500 tracking-tighter">
          {r.receipt_number || "N/A"}
        </span>
      ),
    },
    {
      key: "reference",
      header: "Reference",
      accessor: (r: any) => (
        <span className="font-mono text-[10px] text-slate-500 tracking-tight">
          {r.reference || "—"}
        </span>
      ),
    },
    {
      key: "user",
      header: "Member",
      accessor: (r: any) => (
        <div className="flex flex-col">
          <span className="font-bold text-slate-900 leading-tight">
            {payerName(r)}
          </span>
          <span className="text-[10px] font-black text-slate-400 uppercase tracking-widest">
            {payerTag(r)}
          </span>
        </div>
      ),
    },
    {
      key: "category",
      header: "Category",
      accessor: (r: any) => {
        // Matches the 'user.category' relation in your controller
        const catName =
          r.user?.category?.name || r.user?.user_category?.name ||
          (r.user_id ? "Unassigned" : "Non-member");
        return (
          <div
            className={`inline-flex px-2 py-1 rounded-lg border text-[9px] font-[1000] uppercase tracking-wider ${getCategoryStyle(
              catName,
            )}`}
          >
            {catName}
          </div>
        );
      },
    },
    {
      key: "location",
      header: "State / Chapter",
      accessor: (r: any) => (
        <div className="flex flex-col text-[10px] font-bold uppercase">
          <span className="text-slate-700">
            {r?.user?.member?.state?.name || "N/A"}
          </span>
          <span className="text-slate-400 text-[9px]">
            {r?.user?.member?.chapter?.name || "No Chapter"}
          </span>
        </div>
      ),
    },
    {
      key: "amount",
      header: "Amount",
      accessor: (r: any) => (
        <span className="font-bold text-slate-900">
          {formatCurrency(r.amount)}
        </span>
      ),
    },
    {
      key: "paid_at",
      header: "Date Paid",
      accessor: (r: any) => {
        if (!r.paid_at) return <span className="text-slate-300">—</span>;
        const d = new Date(r.paid_at);
        return (
          <div className="flex flex-col">
            <span className="text-xs font-semibold text-slate-700">
              {d.toLocaleDateString("en-NG", { day: "2-digit", month: "short", year: "numeric" })}
            </span>
            <span className="text-[10px] text-slate-400">
              {d.toLocaleTimeString("en-NG", { hour: "2-digit", minute: "2-digit" })}
            </span>
          </div>
        );
      },
    },
    {
      key: "status",
      header: "Status",
      accessor: (r: any) => {
        if (verifyingIds.has(r.id)) {
          return (
            <div className="inline-flex items-center gap-1 px-2 py-0.5 rounded border text-[10px] font-black uppercase tracking-widest bg-blue-50 text-blue-600 border-blue-100">
              <svg className="animate-spin h-2.5 w-2.5" viewBox="0 0 24 24" fill="none">
                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8H4z" />
              </svg>
              Verifying
            </div>
          );
        }
        return (
          <div
            className={`inline-flex px-2 py-0.5 rounded border text-[10px] font-black uppercase tracking-widest ${
              r.status === "paid"
                ? "bg-emerald-50 text-emerald-600 border-emerald-100"
                : r.status === "failed"
                ? "bg-red-50 text-red-600 border-red-100"
                : "bg-amber-50 text-amber-600 border-amber-100"
            }`}
          >
            {r.status}
          </div>
        );
      },
    },
    {
      key: "recorded_by",
      header: "Recorded By",
      accessor: (r: any) => {
        // Laravel serializes the recordedBy relation as "recorded_by" in JSON,
        // overwriting the raw FK integer. So r.recorded_by is null or a User object.
        const recorder = r.recorded_by;
        if (!recorder) {
          return (
            <span className="text-[10px] font-black text-emerald-600 uppercase tracking-widest">
              Member
            </span>
          );
        }
        const name = typeof recorder === "object" ? recorder.name : null;
        return (
          <span className="text-xs font-semibold text-slate-700">
            {name || "—"}
          </span>
        );
      },
    },
    {
      key: "action",
      header: "Action",
      accessor: rowActions,
    },
  ];

  /**
   * One payment as a card, for screens the ten-column table does not fit.
   *
   * Ordered by what the desk is actually looking for: who paid, how much, and
   * whether it settled. The reference and receipt number sit underneath — they
   * matter when reconciling a specific row, not when scanning the list.
   */
  const mobileCard = (r: any) => {
    const catName =
      r.user?.category?.name || r.user?.user_category?.name ||
      (r.user_id ? "Unassigned" : "Non-member");
    const paidAt = r.paid_at ? new Date(r.paid_at) : null;
    const verifying = verifyingIds.has(r.id);

    return (
      <div className="space-y-3">
        <div className="flex items-start justify-between gap-3">
          <div className="min-w-0">
            <div className="font-bold text-slate-900 leading-tight truncate">
              {payerName(r)}
            </div>
            <div className="text-[10px] font-black text-slate-400 uppercase tracking-widest">
              {payerTag(r)}
            </div>
          </div>
          <div className="text-right shrink-0">
            <div className="font-black text-slate-900">{formatCurrency(r.amount)}</div>
            {paidAt && (
              <div className="text-[10px] text-slate-400 font-semibold">
                {paidAt.toLocaleDateString("en-NG", { day: "2-digit", month: "short", year: "numeric" })}
              </div>
            )}
          </div>
        </div>

        <div className="flex flex-wrap items-center gap-1.5">
          {verifying ? (
            <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded border text-[10px] font-black uppercase tracking-widest bg-blue-50 text-blue-600 border-blue-100">
              <svg className="animate-spin h-2.5 w-2.5" viewBox="0 0 24 24" fill="none">
                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8H4z" />
              </svg>
              Verifying
            </span>
          ) : (
            <span
              className={`inline-flex px-2 py-0.5 rounded border text-[10px] font-black uppercase tracking-widest ${
                r.status === "paid"
                  ? "bg-emerald-50 text-emerald-600 border-emerald-100"
                  : r.status === "failed"
                  ? "bg-red-50 text-red-600 border-red-100"
                  : "bg-amber-50 text-amber-600 border-amber-100"
              }`}
            >
              {r.status}
            </span>
          )}
          <span
            className={`inline-flex px-2 py-0.5 rounded-lg border text-[9px] font-[1000] uppercase tracking-wider ${getCategoryStyle(catName)}`}
          >
            {catName}
          </span>
          <span className="text-[9px] font-black uppercase tracking-widest text-slate-400">
            {r.recorded_by
              ? `By ${typeof r.recorded_by === "object" ? r.recorded_by.name : "admin"}`
              : "Member"}
          </span>
        </div>

        <div className="flex flex-wrap gap-x-3 gap-y-0.5 text-[10px] font-bold text-slate-400 uppercase tracking-wider">
          <span className="font-mono normal-case tracking-tight text-slate-500">
            {r.receipt_number || "No receipt no."}
          </span>
          <span className="font-mono normal-case tracking-tight">
            {r.reference || "—"}
          </span>
          <span>
            {r?.user?.member?.state?.name || "N/A"}
            {" · "}
            {r?.user?.member?.chapter?.name || "No Chapter"}
          </span>
        </div>

        {rowActions(r)}
      </div>
    );
  };

  const handleExport = async () => {
    setIsExporting(true);
    try {
      const params = new URLSearchParams({ sort_by: sort, sort_dir: dir });
      Object.entries(currentFilters).forEach(([k, v]) => {
        if (v) params.set(k, String(v));
      });

      const token = localStorage.getItem("token");
      const response = await fetch(
        `${BASE_URL}/admin/billing/admin/payments/export?${params.toString()}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );

      if (!response.ok) throw new Error("Export request failed");

      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = "payments_export.csv";
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      window.URL.revokeObjectURL(url);
    } catch (err) {
      console.error("CSV export failed:", err);
    } finally {
      setIsExporting(false);
    }
  };

  return (
    <div className="space-y-4 sm:space-y-6">

      {/* Action toast. Pinned to the top on a phone, where a corner toast is
          half off-screen. */}
      {actionToast && (
        <div className={`fixed top-4 left-4 right-4 sm:left-auto sm:top-6 sm:right-6 z-50 px-4 py-3 rounded-2xl shadow-xl text-sm font-bold text-white ${
          actionToast.tone === "error" ? "bg-rose-600" : "bg-emerald-600"
        }`}>
          {actionToast.text}
        </div>
      )}

      {/* Pending edit/delete requests banner */}
      {pendingRequestCount > 0 && (
        <Link
          to="/finance/payment-requests"
          className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2 bg-orange-50 border border-orange-200 rounded-2xl px-4 sm:px-5 py-3 hover:bg-orange-100 transition-all"
        >
          <div className="flex items-center gap-3">
            <span className="flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-orange-500 text-white text-[10px] font-black">
              {pendingRequestCount}
            </span>
            <span className="text-xs sm:text-sm font-black text-orange-800">
              Pending payment edit / delete request{pendingRequestCount !== 1 ? "s" : ""} awaiting your approval
            </span>
          </div>
          <span className="text-[10px] font-black uppercase tracking-widest text-orange-600 shrink-0">
            Review →
          </span>
        </Link>
      )}

      {/* 1. FINANCIAL ANALYSIS CARDS */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-3 sm:gap-4">
        <div className="bg-slate-900 p-5 sm:p-6 rounded-3xl sm:rounded-[2rem] text-white shadow-xl shadow-slate-200 border-b-4 border-orange-500">
          <p className="text-[10px] font-black text-slate-400 uppercase tracking-widest">
            Total Filtered Revenue
          </p>
          {/* The figure is the widest thing on the page; it has to shrink with
              the screen rather than push the card open. */}
          <p className="text-2xl sm:text-3xl font-[1000] text-white tracking-tighter break-words">
            {formatCurrency(stats.total_sum)}
          </p>
          <p className="text-[10px] font-bold text-slate-500 mt-1 uppercase tracking-widest">
            {stats.total_count} transactions
          </p>
        </div>

        <div className="bg-white border border-slate-200 p-5 sm:p-6 rounded-3xl sm:rounded-[2rem] md:col-span-2 shadow-sm overflow-hidden flex flex-col justify-center">
          <p className="text-[10px] font-black text-slate-400 uppercase tracking-widest mb-3">
            Revenue By Category
          </p>
          <div className="flex gap-4 overflow-x-auto no-scrollbar pb-2 -mx-1 px-1">
            {stats.by_category?.map((c: any) => (
              <div
                key={c.label}
                className="flex-shrink-0 border-l-2 border-orange-200 pl-3"
              >
                <p className="text-[9px] font-black text-slate-400 uppercase truncate max-w-[100px]">
                  {c.label}
                </p>
                <p className="text-sm font-black text-slate-800">
                  {formatCurrency(c.total)}
                </p>
              </div>
            ))}
          </div>
        </div>

        <div className="bg-white border border-slate-200 p-5 sm:p-6 rounded-3xl sm:rounded-[2rem] shadow-sm">
          <p className="text-[10px] font-black text-slate-400 uppercase tracking-widest mb-2">
            Collection Health
          </p>
          {stats.by_status?.map((s: any) => (
            <div
              key={s.status}
              className="flex justify-between items-center gap-2 text-[10px] font-black mb-1"
            >
              <span className="text-slate-400 uppercase">{s.status}</span>
              <span
                className={
                  s.status === "paid" ? "text-emerald-600" : "text-amber-600"
                }
              >
                {formatCurrency(s.total)}
              </span>
            </div>
          ))}
        </div>
      </div>

      {/* 2. FILTER BAR */}
      <div className="bg-white p-4 sm:p-6 rounded-3xl sm:rounded-[2rem] border border-slate-200 shadow-sm space-y-4">
        {/* Search stays out in the open — it is the one filter the desk reaches
            for every time. The other six fold away on a phone, where seven
            stacked controls would push the ledger itself off the screen. */}
        <div className="flex gap-2 lg:hidden">
          <input
            placeholder="Search Member/Ref..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="flex-1 min-w-0 border px-4 py-2.5 rounded-xl text-sm bg-slate-50 outline-none focus:ring-2 ring-orange-100 transition-all"
          />
          <button
            onClick={() => setShowFilters((p) => !p)}
            className="shrink-0 flex items-center gap-1.5 border px-3 py-2.5 rounded-xl text-[10px] font-black uppercase tracking-widest text-slate-600 bg-slate-50"
          >
            Filters
            {activeFilterCount > 0 && (
              <span className="flex h-4 w-4 items-center justify-center rounded-full bg-orange-500 text-white text-[9px]">
                {activeFilterCount}
              </span>
            )}
          </button>
        </div>

        <div className={`${showFilters ? "grid" : "hidden"} lg:grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-7 gap-3`}>
          <input
            placeholder="Search Member/Ref..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="hidden lg:block border px-4 py-2 rounded-xl text-sm bg-slate-50 outline-none focus:ring-2 ring-orange-100 transition-all"
          />

          <select
            value={status}
            onChange={(e) => setStatus(e.target.value)}
            className="border px-4 py-2.5 lg:py-2 rounded-xl text-sm bg-slate-50 outline-none"
          >
            <option value="">Status</option>
            <option value="paid">Paid</option>
            <option value="pending">Pending</option>
          </select>

          <select
            value={categoryId}
            onChange={(e) => setCategoryId(e.target.value)}
            className="border px-4 py-2.5 lg:py-2 rounded-xl text-sm bg-slate-50 outline-none font-bold text-orange-600"
          >
            <option value="">All Categories</option>
            {lookups.categories.map((c: any) => (
              <option key={c.id} value={c.id}>
                {c.name}
              </option>
            ))}
          </select>

          <select
            value={stateId}
            onChange={(e) => setStateId(e.target.value)}
            className="border px-4 py-2.5 lg:py-2 rounded-xl text-sm bg-slate-50 outline-none"
          >
            <option value="">All States</option>
            {lookups.states.map((s: any) => (
              <option key={s.id} value={s.id}>
                {s.name}
              </option>
            ))}
          </select>

          <select
            value={chapterId}
            onChange={(e) => setChapterId(e.target.value)}
            className="border px-4 py-2.5 lg:py-2 rounded-xl text-sm bg-slate-50 outline-none"
          >
            <option value="">All Chapters</option>
            {lookups.chapters?.map((ch: any) => (
              <option key={ch.id} value={ch.id}>
                {ch.name}
              </option>
            ))}
          </select>

          <select
            value={adminId}
            onChange={(e) => setAdminId(e.target.value)}
            className="border px-4 py-2.5 lg:py-2 rounded-xl text-sm bg-slate-50 outline-none"
          >
            <option value="">By Admin</option>
            {lookups.admins.map((a: any) => (
              <option key={a.id} value={a.id}>
                {a.name}
              </option>
            ))}
          </select>

          <button
            onClick={() => {
              setSearch("");
              setStatus("");
              setAdminId("");
              setCategoryId("");
              setStateId("");
              setChapterId("");
              setStartDate("");
              setEndDate("");
            }}
            className="text-rose-600 text-[10px] font-black uppercase tracking-widest hover:bg-rose-50 py-2.5 lg:py-0 px-2 rounded-xl transition-all border lg:border-0 border-rose-100"
          >
            Reset
          </button>
        </div>

        <div className={`${showFilters ? "flex" : "hidden"} lg:flex flex-col sm:flex-row flex-wrap gap-3 sm:gap-4 sm:items-center border-t border-slate-100 pt-4`}>
          <div className="flex items-center gap-2 flex-wrap">
            <span className="text-[10px] font-black text-slate-400 uppercase tracking-widest">
              Date Range
            </span>
            <input
              type="date"
              value={startDate}
              onChange={(e) => setStartDate(e.target.value)}
              className="flex-1 min-w-0 sm:flex-none border px-3 py-2 rounded-xl text-xs bg-slate-50 outline-none"
            />
            <input
              type="date"
              value={endDate}
              onChange={(e) => setEndDate(e.target.value)}
              className="flex-1 min-w-0 sm:flex-none border px-3 py-2 rounded-xl text-xs bg-slate-50 outline-none"
            />
          </div>

          <div className="sm:ml-auto flex items-center gap-2">
            <span className="text-[10px] font-black text-slate-400 uppercase tracking-widest">
              Show
            </span>
            <select
              value={perPage}
              onChange={(e) => setPerPage(Number(e.target.value))}
              className="border px-3 py-1.5 rounded-xl text-xs bg-slate-50 outline-none"
            >
              <option value={25}>25</option>
              <option value={50}>50</option>
              <option value={100}>100</option>
            </select>
          </div>
        </div>
      </div>

      {/* 3. MAIN LEDGER — table on desktop, cards on a phone */}
      <div className="bg-white rounded-3xl sm:rounded-[2rem] border border-slate-200 shadow-sm overflow-hidden">
        {isLoading ? (
          <TableSkeleton />
        ) : (
          <DataTable
            columns={columns}
            data={paymentList}
            pagination={pagination}
            onPageChange={setPage}
            sort={sort}
            setSort={setSort}
            dir={dir}
            setDir={setDir}
            selected={[]}
            setSelected={() => {}}
            onExport={handleExport}
            isExporting={isExporting}
            mobileCard={mobileCard}
          />
        )}
      </div>

      {/* Request Edit Modal */}
      {editTarget && (
        <RequestPaymentEditModal
          payment={editTarget}
          onClose={() => setEditTarget(null)}
          onSuccess={() => {
            setEditTarget(null);
            showActionToast("Edit request submitted. Awaiting approval from another admin.");
          }}
        />
      )}

      {/* Request Delete Modal */}
      {deleteTarget && (
        <RequestPaymentDeleteModal
          payment={deleteTarget}
          onClose={() => setDeleteTarget(null)}
          onSuccess={() => {
            setDeleteTarget(null);
            showActionToast("Delete request submitted. Awaiting approval from another admin.");
          }}
        />
      )}

      {/* Audit Trail Modal */}
      {trailTarget && (
        <PaymentAuditTrailModal
          payment={trailTarget}
          onClose={() => setTrailTarget(null)}
        />
      )}
    </div>
  );
}
